import { Money, type BillingPreference, type Currency, type UpdateCustomerInput } from '@chukua/common';
import { Alert, Button, Form, Input, Switch } from 'antd';
import { useEffect } from 'react';
import { BillingAddressFields } from './BillingAddressFields';
import type { CustomerView } from '../customers.api';
import { TermsAndCreditFields } from './TermsAndCreditFields';

interface DetailsFormValues {
  name: string;
  kraPin: string;
  billingAddressLine1: string;
  billingAddressLine2?: string;
  billingAddressCity: string;
  billingAddressCountry: string;
  paymentTermsDays: number;
  creditLimitAmount: number;
  creditLimitCurrency: Currency;
  billingPreference: BillingPreference;
  poNumberRequired: boolean;
  isContracted: boolean;
}

interface CustomerDetailsFormProps {
  customer: CustomerView;
  canManage: boolean;
  error: string | null;
  saving: boolean;
  onSave: (input: UpdateCustomerInput) => Promise<boolean>;
}

function toFormValues(customer: CustomerView): DetailsFormValues {
  const currency = customer.creditLimit.currency as Currency;
  return {
    name: customer.name,
    kraPin: customer.kraPin,
    billingAddressLine1: customer.billingAddress.line1,
    billingAddressLine2: customer.billingAddress.line2,
    billingAddressCity: customer.billingAddress.city,
    billingAddressCountry: customer.billingAddress.country,
    paymentTermsDays: customer.paymentTermsDays,
    creditLimitAmount: Number(Money.ofMinor(customer.creditLimit.amountMinor, currency).toDecimalString()),
    creditLimitCurrency: currency,
    billingPreference: customer.billingPreference,
    poNumberRequired: customer.poNumberRequired,
    isContracted: customer.isContracted,
  };
}

function toUpdateInput(values: DetailsFormValues): UpdateCustomerInput {
  return {
    name: values.name,
    kraPin: values.kraPin,
    billingAddress: {
      line1: values.billingAddressLine1,
      line2: values.billingAddressLine2 || undefined,
      city: values.billingAddressCity,
      country: values.billingAddressCountry,
    },
    paymentTermsDays: values.paymentTermsDays,
    creditLimit: Money.parse(String(values.creditLimitAmount), values.creditLimitCurrency).toJSON(),
    billingPreference: values.billingPreference,
    poNumberRequired: values.poNumberRequired,
    // No form field yet (gitaumoses4/chukua#29 is API + common only) — carries the existing value through unchanged rather than resetting it.
    isContracted: values.isContracted,
  };
}

/** The customer's fields, editable inline for `customer.manage` holders — read-only for everyone else with `customer.view` (CLAUDE.md: nav-hiding and disabled controls are UX only, the server remains the authority). */
export function CustomerDetailsForm({ customer, canManage, error, saving, onSave }: CustomerDetailsFormProps) {
  const [form] = Form.useForm<DetailsFormValues>();

  useEffect(() => {
    form.setFieldsValue(toFormValues(customer));
  }, [customer, form]);

  async function submit(values: DetailsFormValues) {
    await onSave(toUpdateInput(values));
  }

  return (
    <Form<DetailsFormValues>
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={submit}
      disabled={!canManage || saving}
      initialValues={toFormValues(customer)}
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <div className="grid grid-cols-2 gap-3">
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="kraPin"
          label="KRA PIN"
          rules={[{ required: true, pattern: /^[A-Za-z]\d{9}[A-Za-z]$/, message: 'One letter, nine digits, one letter' }]}
        >
          <Input className="uppercase" />
        </Form.Item>
      </div>
      <BillingAddressFields />
      <TermsAndCreditFields />
      <Form.Item name="poNumberRequired" label="PO number required" valuePropName="checked">
        <Switch />
      </Form.Item>
      {canManage && (
        <Form.Item className="mb-0">
          <Button type="primary" htmlType="submit" loading={saving}>
            Save changes
          </Button>
        </Form.Item>
      )}
    </Form>
  );
}
